import { ACTION_STATUS_LABELS, type ActionPriority, type ActionStatus, type AuditMode } from '@bp/shared';
import { AlertTriangle, CheckCircle2, FlaskConical, Lock, ShieldCheck } from 'lucide-react';
import { Badge, type Tone } from './ui';
import { cn, scoreTone } from '@/lib/utils';

export function ModeBadge({ mode }: { mode: AuditMode }) {
  return mode === 'practice'
    ? <Badge tone="practice"><FlaskConical className="size-3" />Practice</Badge>
    : <Badge tone="info"><ShieldCheck className="size-3" />Official</Badge>;
}

export function ScoreBadge({ score, passing, warning, className }: { score: number | null | undefined; passing?: number; warning?: number; className?: string }) {
  const tone = scoreTone(score, passing, warning);
  if (tone === 'none') return <Badge className={className}>No score</Badge>;
  return <Badge tone={tone} className={cn('tabular', className)}>{score}%</Badge>;
}

const statusTone: Record<string, Tone> = { passing: 'pass', warning: 'warn', failing: 'fail', no_audits: 'neutral' };
const statusLabel: Record<string, string> = { passing: 'Passing', warning: 'Warning', failing: 'Failing', no_audits: 'No official audits' };
export function StoreStatusBadge({ status }: { status: string | null | undefined }) {
  const s = status ?? 'no_audits';
  return <Badge tone={statusTone[s] ?? 'neutral'}>{statusLabel[s] ?? s}</Badge>;
}

export function AuditStatusBadge({ status, locked }: { status: string; locked: boolean }) {
  if (status === 'in_progress') return <Badge tone="warn">In progress</Badge>;
  return <Badge tone="pass">{locked ? <Lock className="size-3" /> : <CheckCircle2 className="size-3" />}Completed</Badge>;
}

const actionTone: Record<ActionStatus, Tone> = { open: 'neutral', in_progress: 'info', submitted: 'practice', verified: 'pass', reopened: 'warn' };
export function ActionStatusBadge({ status, overdueDays }: { status: ActionStatus; overdueDays?: number }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone={actionTone[status]}>{ACTION_STATUS_LABELS[status]}</Badge>
      {!!overdueDays && <Badge tone="fail"><AlertTriangle className="size-3" />{overdueDays}d overdue</Badge>}
    </span>
  );
}

const prTone: Record<ActionPriority, Tone> = { low: 'neutral', medium: 'info', high: 'warn', critical: 'fail' };
export function PriorityBadge({ priority }: { priority: ActionPriority }) {
  return <Badge tone={prTone[priority]} className="capitalize">{priority}</Badge>;
}
